import type { ABTestConfigs } from "../utils/ABTestingCore";
import { createABTestManager } from "../utils/ABTestingCore";
import type { KinkQuizFlowVersion } from "../services/kinkQuizFlowVersion";

/** Which kind of first chat a quiz completer is routed into — see `postQuizChatMode`. */
export type PostQuizChatMode = "scripted" | "ai";

/**
 * Tests that apply to the whole app, independent of any onboarding flow.
 *
 * (The `/paywall/subscribe` design test — control / candy / dreams — used to
 * live here; it concluded on 2026-08-28 with "candy" winning, and that layout
 * is now the page itself, see ../pages/paywall/variants/CandySubscribeLayout.tsx.)
 */
export const globalABTests: ABTestConfigs = {
  /**
   * **What the quiz hands the user when it ends: an authored scripted scene or
   * an ordinary AI chat.** Started 2026-08-28.
   *
   * - "scripted" — the first chat plays an authored scene. **Since 2026-09-03
   *   the character is matched by looks over the whole pool** — every
   *   scripted-chat pilot (`activeScriptId`) plus every private, non-premium
   *   character with spicy media (`getScriptedMatchingPool`) — and
   *   `create-chat` attaches a script to the chat: the pilot's own when the
   *   match is a pilot, her generated `onboardingScriptId` script when she has
   *   one (2026-09-04, apps/scripts/scripted-chat/), otherwise a shared active
   *   script (`scriptState.scriptSource` says character vs shared;
   *   `scriptState.scriptId` starting with `script-<characterId>` marks a
   *   generated one). Before that date matching was
   *   restricted to the six pilots, which is why a user asking for a redhead
   *   or a Black woman got "the nearest pilot" — see
   *   ../../docs/scripted-chat-visual-matching-plan.md.
   * - "ai" — matching runs over the ordinary private-character pool with every
   *   `activeScriptId` carrier and every `isPremiumOnly` character removed (71
   *   characters on production as of 2026-08-28). The matched character has no
   *   script — and since 2026-09-03 `planOpening` also refuses one for this
   *   arm server-side — so the user lands in a normal AI chat with the usual
   *   opening message.
   *
   * Resolved in exactly one place — `matchPrivateCharacterFromOnboarding` in
   * ../services/userService.tsx — because that function has three call sites
   * (OnboardingRouter's `completeOnboarding`, SummaryScreen, and
   * OnboardingSuccessScreen's re-match with `excludedCharacterIds`) and a user
   * must never get one arm on the first match and the other on a re-match.
   *
   * Deliberately a *global* test, not a flow-scoped one: it applies to every
   * kink-quiz flow and is resolved from a service, where `flow.name` is not in
   * scope. Exposure is logged from SummaryScreen — see the `pages` key below
   * and the comment on the `useABTestExposure` call there.
   *
   * **What an analyst must know:**
   * 1. Pool breadth used to be confounded with scripted-ness (6 pilots vs 71
   *    characters). Since the 2026-09-03 deploy both arms match over roughly
   *    the same pool, so `scripted`-arm chats before and after that deploy are
   *    not comparable with each other — split on the deploy time or on
   *    `scriptState.scriptSource` ("shared" only exists after it).
   * 2. Scripted turns bypass wallet charging entirely
   *    (apps/functions/src/functions/send-message.ts returns before the
   *    charge), so the "ai" arm burns its signup bonus faster and hits the
   *    token paywall sooner. A purchase-rate difference may be wallet
   *    depletion, not desire.
   *
   * **Deploy `apps/functions` before or together with `apps/web`.** The arm is
   * stamped onto the chat document by the `createChat` callable, which drops
   * the field until the new server code is live; a chat created in that window
   * is permanently unattributable (the "ai" arm writes no `scriptState`
   * either). See the `postQuizChatMode` section of the repo CLAUDE.md for the
   * commands and the staging verification step.
   *
   * No `version` on purpose. `getVariantStorageKey` (see
   * ../utils/ABTestingCore.ts) only appends `_v<n>` when `version > 1`, so this
   * test stores under `global_postQuizChatModeKey` — a key that has never been
   * written by any earlier test (`globalABTests` was empty, and the retired
   * paywall test used its own `subscribePaywallDesignKey`). There is therefore
   * no stale bucket to escape, which is the only thing `version` is for.
   */
  postQuizChatMode: {
    key: "postQuizChatModeKey",
    variants: [
      { name: "scripted", weight: 1 },
      { name: "ai", weight: 1 },
    ],
    // Exposure key, logged from SummaryScreen: `summary` is the first screen
    // where the arm is visible to the user (it renders the matched
    // character's name and photo, and it is the screen whose effect calls
    // `matchPrivateCharacterFromOnboarding` for the first time).
    pages: ["summary"],
  },
};

/**
 * Scope for `kinkQuizVersion` only. Deliberately NOT a flow name: that test
 * decides *which* flow document to fetch, so `OnboardingPage` has to resolve it
 * before `flow.name` exists. Keeping it in its own fixed scope means the
 * assignment is stable no matter which flow it selects.
 *
 * Everything else stays scoped by `flow.name` (see `onboardingABTests`), which
 * `OnboardingRouter` resolves once the document has loaded.
 */
export const KINK_QUIZ_VERSION_SCOPE = "kinkquiz";

/**
 * Flow names (`flow.name` in the Firestore documents) that the kink quiz serves.
 * Every name listed here gets the flow-scoped tests below registered for it;
 * a name that is missing is silently dropped by `getOnboardingABTests`, so a
 * new flow must be added here as well as to the `kinkQuizVersion` variants.
 */
export const KINK_QUIZ_FLOW_NAMES = ["v2", "v3", "v4", "v5"] as const;

/**
 * Tests that apply to any onboarding flow regardless of `flow.name`.
 *
 * Intentionally empty: `landingSubtitle` and `summaryImageBlur` were both
 * retired on 2026-08-14 (the former had been a no-op for weeks; the latter was
 * concluded in favour of the unblurred summary image). Kept as the extension
 * point for the next flow-agnostic test — `getOnboardingABTests` spreads it
 * under the per-flow tests below.
 */
export const onboardingABTestsDefault: ABTestConfigs = {};

/** Tests that apply to every kink-quiz flow, registered per `flow.name` below. */
const kinkQuizFlowTests: ABTestConfigs = {
  /**
   * Whether the in-quiz paywall/checkout steps are reachable at all.
   *
   * "shown" is kept at weight 0 (nobody is newly assigned to it) so the code
   * path stays alive, but conversion for the scripted-chat-aligned flows
   * (v4 and now v5) happens inside the scripted scene's media unlocks instead
   * of an in-quiz paywall — see apps/competition/mechat/scripted-chat-spec.md.
   * To roll everyone back onto "hidden" at once — including users who already
   * have "shown" in localStorage — set FORCED_PAYWALL_VISIBILITY below (same
   * mechanism as FORCED_PAYWALL_VERSION).
   */
  paywallVisibility: {
    key: "paywallVisibilityKey",
    variants: [
      // 2026-08-10 (v4 + scripted-chat production rollout; unchanged by the
      // 2026-08-14 v5 switch): hidden for everyone — conversion happens
      // inside the scripted scene's media unlocks instead of an in-quiz
      // paywall (see the block comment above). "shown" stays a valid name
      // so the code path and any stored assignments remain recognized.
      { name: "hidden", weight: 1 },
      { name: "shown", weight: 0 },
    ],
    pages: ["paywall"],
  },
  /**
   * Which onboarding paywall is rendered:
   * - "match"  — the quiz-continuous redesign (OnboardingPaywallMatchScreen)
   * - "legacy" — the pre-redesign screen (OnboardingPaywallScreen)
   *
   * Legacy is kept at weight 0: nobody is assigned to it, but the code path
   * stays alive so the old paywall can be brought back. To split traffic for a
   * real comparison, give legacy a weight (e.g. both 1). To roll everyone back
   * at once — including users who already have "match" in localStorage — set
   * FORCED_PAYWALL_VERSION below.
   */
  paywallVersion: {
    key: "paywallVersionKey",
    variants: [
      { name: "match", weight: 1 },
      { name: "legacy", weight: 0 },
    ],
    pages: ["paywall"],
  },
};

export const onboardingABTests: Record<string, ABTestConfigs> = {
  // Same tests for every kink-quiz flow, so switching versions never silently
  // drops a test out of scope.
  ...Object.fromEntries(KINK_QUIZ_FLOW_NAMES.map((name) => [name, kinkQuizFlowTests])),
  [KINK_QUIZ_VERSION_SCOPE]: {
    /**
     * Which kink-quiz flow document a user's onboarding attempt loads.
     * **Since 2026-08-28 this is a real 50/50 test between v5 and v4** — see
     * the weights and the `version: 2` note below.
     *
     * - "v5" — the short flow (`ai_girl_v5_english` / `ai_girl_v5`): the
     *   radically shortened quiz, 11 steps / 5 answerable questions, with no
     *   `info`, `statements` or `kinkResult` screens — the archetype is
     *   derived silently from the `dynamic` answer (see
     *   buildImplicitKinkProfile in ../services/kinkProfileService.ts) and
     *   surfaced as a line on `summary`. Like v4 it leads into a matched pilot
     *   character's authored scripted scene.
     * - "v4" — the long flow (`ai_girl_v4_english` / `ai_girl_v4`), the
     *   scripted-chat-aligned 24-step quiz (see
     *   apps/competition/mechat/scripted-chat-spec.md). **This is the other
     *   half of the running test, and the rollback target.**
     * - "v3" — the flow before that (`ai_girl_v3_english` / `ai_girl_v3`).
     * - "v2" — the oldest still-recognized flow (`ai_girl_v2_english` /
     *   `ai_girl_v2`).
     *
     * "v3"/"v2" are kept at weight 0 rather than deleted: a name that is still
     * registered stays a valid stored variant (see resolveKinkQuizFlowId and
     * readVariantOverride), so `?ab_kinkQuizVersion=v3|v2` still resolves on
     * dev/staging. They are never assigned by weight.
     *
     * **2026-08-28 — the v5-vs-v4 test is live.** v5 and v4 carry equal weight
     * for a true 50/50 split of all onboarding traffic, and
     * FORCED_KINK_QUIZ_VERSION below is back to `null` so the assignment is
     * what actually decides the flow (a non-null value there would win over it
     * and there would be no test at all).
     *
     * `version: 2` is load-bearing, not decoration. The assignment is stored
     * under `${scopeKey}_${key}` with no version in it, and
     * `getOrCreateVariant` keeps any stored variant that is still a registered
     * name — so without a version bump this test would have inherited the
     * pre-test buckets: everyone carrying "v5" from the 100% rollout would stay
     * on v5 (no split), and anyone bucketed before 2026-08-14 would be silently
     * dropped onto v3/v2 the moment the forced constant went away. Bumping the
     * version changes the storage key (`kinkquiz_kinkQuizVersionKey_v2`, see
     * getVariantStorageKey in ../utils/ABTestingCore.ts), so every visitor —
     * new and returning — is re-bucketed 50/50 by the weights above, and it
     * also splits this test's exposure label (`kinkQuizVersion-v2_v5|v4`) from
     * the old one's in analytics.
     *
     * **To end the test: set FORCED_KINK_QUIZ_VERSION below to the winner
     * ("v5" or "v4") — one line, one deploy.** That is clean either way:
     * production Firestore holds both the `ai_girl_v5*` and the `ai_girl_v4*`
     * flow documents, and the pilots retired in the v5 switch
     * (Margot/Mina/Elise) kept their `scriptMediaMap`, so v4's quiz and its
     * scripted chats work exactly as before. Afterwards the weights can be
     * squared up with the decision (which only affects newly bucketed
     * visitors) and the constant put back to null.
     *
     * Lives in its own scope, not a flow scope — see KINK_QUIZ_VERSION_SCOPE.
     */
    kinkQuizVersion: {
      key: "kinkQuizVersionKey",
      // Bumped from 1 on 2026-08-28 to force every visitor out of their
      // pre-test bucket — see the block comment above. Do not lower it.
      version: 2,
      variants: [
        { name: "v5", weight: 1 },
        { name: "v4", weight: 1 },
        // Weight 0 on purpose: nobody is bucketed into these, but they stay
        // registered so `?ab_kinkQuizVersion=v3|v2` still resolves on
        // dev/staging (readVariantOverride rejects any unregistered value).
        { name: "v3", weight: 0 },
        { name: "v2", weight: 0 },
      ],
      pages: ["landing"],
    },
  },
};

export type OnboardingPaywallVersion = "match" | "legacy";
export type OnboardingPaywallVisibility = "shown" | "hidden";

/**
 * Emergency override for `paywallVersion`. `null` = respect the A/B assignment.
 * Set to "legacy" (one line, one deploy) to force every user back onto the old
 * paywall regardless of what is stored in their browser.
 */
export const FORCED_PAYWALL_VERSION: OnboardingPaywallVersion | null = null;

/**
 * Override for `paywallVisibility`, winning over both the A/B assignment and
 * any variant already stored in the visitor's browser — same mechanism as
 * FORCED_PAYWALL_VERSION. `null` = respect the A/B assignment.
 *
 * Currently "hidden": the in-quiz paywall is off for everyone, including
 * anyone already bucketed onto "shown" before this rollout. Conversion for
 * the scripted-chat-aligned quizzes (v4, and v5 since 2026-08-14) happens
 * inside the scripted scene's media unlocks instead — see
 * apps/competition/mechat/scripted-chat-spec.md.
 * Safe to set back to null once stored "shown" values have aged out; the
 * weights above then keep everyone on "hidden" anyway.
 *
 * 2026-08-10: "hidden" again — the v4 + scripted-chat rollout is live on
 * production (data seeded, see FORCED_KINK_QUIZ_VERSION above), so
 * conversion happens in the scripted scene's media unlocks and the in-quiz
 * paywall is off for everyone, including previously bucketed "shown" users.
 */
export const FORCED_PAYWALL_VISIBILITY: OnboardingPaywallVisibility | null = "hidden";

/**
 * Override for `kinkQuizVersion`, winning over both the A/B assignment and any
 * variant already stored in the visitor's browser. `null` = respect the A/B
 * assignment.
 *
 * **This is the lever that ends the v5-vs-v4 test.** Set it to the winner
 * ("v5" or "v4") — one line, one deploy — and every visitor is served that
 * flow immediately, including anyone the test had bucketed onto the other one.
 * It is also the emergency stop if one arm turns out to be broken. Both arms
 * are safe to force: production Firestore holds the `ai_girl_v5*` docs (11
 * steps each), the nine `characterScripts` and the six-pilot pool (Brooke,
 * Isabel, Miki, Natalie, Lina, Vesper) with `activeScriptId`/`scriptMediaMap`,
 * *and* the untouched `ai_girl_v4*` docs whose retired pilots
 * (Margot/Mina/Elise) kept their `scriptMediaMap`.
 *
 * 2026-08-28: back to `null`. It was "v5" from the 2026-08-14 100% rollout;
 * leaving it set would beat the new 50/50 assignment and there would be no
 * test at all. Re-bucketing of the visitors it used to cover is handled by
 * `version: 2` on the `kinkQuizVersion` config above, not by this constant.
 *
 * While this is null the exposure events and the effective flow agree. If it
 * is ever set again, note the same caveat as FORCED_PAYWALL_VISIBILITY: the
 * `kinkQuizVersion-v2_<bucket>` exposure keeps reporting the raw localStorage
 * bucket, while `onboardingName` on the onboarding events reports the
 * *effective* flow — group by `onboardingName` in that case.
 *
 * On dev/staging the `?ab_kinkQuizVersion=` URL override wins over this
 * constant (see OnboardingPage), so v4/v3/v2 stay testable there.
 */
export const FORCED_KINK_QUIZ_VERSION: KinkQuizFlowVersion | null = null;

/**
 * Override for `postQuizChatMode`, winning over both the A/B assignment and
 * any variant already stored in the visitor's browser — same mechanism as
 * FORCED_PAYWALL_VERSION / FORCED_KINK_QUIZ_VERSION. `null` = respect the A/B
 * assignment, which is what a running test needs.
 *
 * **This is the lever that ends the scripted-vs-AI test.** Set it to the
 * winner ("scripted" or "ai") — one line, one deploy — and every quiz
 * completer is routed that way immediately, including anyone the test had
 * bucketed onto the other arm. It is also the emergency stop if one arm turns
 * out to be broken; "scripted" restores the pre-test behaviour exactly.
 *
 * Same caveat as every FORCED_* constant: the exposure event keeps reporting
 * the raw localStorage bucket, so once this is non-null the exposure and the
 * effective arm disagree — group by `postQuizChatMode` on the chat document
 * (stamped by `create-chat`) or by the `post_quiz_chat_mode` param on
 * `media_unlocked` instead.
 *
 * On dev/staging the `?ab_postQuizChatMode=scripted|ai` URL override wins over
 * this constant (see resolvePostQuizChatMode in ../services/userService.tsx),
 * so both arms stay testable there after the test ends.
 */
export const FORCED_POST_QUIZ_CHAT_MODE: PostQuizChatMode | null = null;

export function getOnboardingABTests(flowName?: string): ABTestConfigs {
  if (!flowName) return onboardingABTestsDefault;
  const flowTests = onboardingABTests[flowName] ?? {};
  return { ...onboardingABTestsDefault, ...flowTests };
}

export function createGlobalABTestManager() {
  return createABTestManager(globalABTests, { scopeKey: "global" });
}

export function createOnboardingABTestManager(flowName?: string) {
  const scopeKey = flowName || "onboarding";
  return createABTestManager(getOnboardingABTests(flowName), { scopeKey });
}
